import { useMemo, useState } from 'react';
import { useOrcamentos } from '@/features/orcamentos/hooks/useOrcamentos';
import { useFiltrosKanban } from '@/features/orcamentos/hooks/useFiltrosKanban';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROTAS } from '@/app/rotas';
import { OrcamentoNovoPage } from '@/features/orcamentos/pages/OrcamentoNovoPage';
import { OrcamentoVisualizacaoPage } from '@/features/orcamentos/pages/OrcamentoVisualizacaoPage';
import {
  type OrcamentoResumo,
  type StatusOrcamento,
} from '@/features/orcamentos/api/orcamentosApi';
import {
  ETAPA_CANCELADO,
  ETAPA_FECHADO,
  ehTerminal,
  ordenarEtapas,
  textoConfirmacao,
  type EtapaKanban,
} from '@/features/orcamentos/api/etapasKanban';
import { useEtapasKanban } from '@/shared/state/EtapasKanbanContext';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Modal } from '@/shared/ui/Modal';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SearchInput, normalizarBusca } from '@/shared/ui/SearchInput';
import {
  IconDollar,
  IconEdit,
  IconEye,
  IconInbox,
  IconRefresh,
  IconTrash,
} from '@/shared/ui/Icon';
import { CriarContaReceberDoOrcamentoModal } from '@/features/financeiro/pages/CriarContaReceberDoOrcamentoModal';
import { useDialogos } from '@/shared/ui/DialogosProvider';
import { BotaoOcultarValores } from '@/shared/ui/BotaoOcultarValores';
import {
  formatarValorMonetario,
  useOcultarValores,
} from '@/shared/state/OcultarValoresContext';
import { useNotasAvaliacao } from '@/features/pos-venda/hooks/useNotasAvaliacao';
import { NotaAvaliacaoKanban } from '@/features/pos-venda/components/NotaAvaliacaoKanban';

const formatadorData = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

function formatarData(iso: string) {
  return formatadorData.format(new Date(iso));
}

/**
 * true se a data de entrega já passou (comparando só a data, no fuso local).
 * Cada etapa decide se quer o destaque — "aguardando pagamento" e as etapas
 * finais, por exemplo, não avisam de atraso porque o serviço já saiu.
 */
function entregaAtrasada(prazoEntregaIso: string, alertaAtraso: boolean) {
  if (!alertaAtraso) return false;
  const prazo = new Date(prazoEntregaIso);
  if (Number.isNaN(prazo.getTime())) return false;
  const hoje = new Date();
  const inicioHoje = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate());
  const inicioPrazo = new Date(prazo.getFullYear(), prazo.getMonth(), prazo.getDate());
  return inicioPrazo.getTime() < inicioHoje.getTime();
}

type KanbanProps = {
  orcamentos: OrcamentoResumo[];
  /** Colunas na ordem configurada, já incluindo etapas órfãs dos dados. */
  colunas: EtapaKanban[];
  aoVisualizar: (id: string) => void;
  aoEditar: (id: string) => void;
  aoCriarContaReceber: (id: string) => void;
  aoExcluir: (o: OrcamentoResumo) => void;
  aoTrocarStatus: (o: OrcamentoResumo, novo: StatusOrcamento) => Promise<void>;
};

function Kanban({
  orcamentos,
  colunas,
  aoVisualizar,
  aoEditar,
  aoCriarContaReceber,
  aoExcluir,
  aoTrocarStatus,
}: KanbanProps) {
  const { ocultar } = useOcultarValores();
  const [arrastando, setArrastando] = useState<string | null>(null);
  const [colunaAlvo, setColunaAlvo] = useState<StatusOrcamento | null>(null);
  // Nota da avaliação do cliente (pós-venda), em lote para os cards visíveis.
  const idsVisiveis = useMemo(() => orcamentos.map((o) => o.id), [orcamentos]);
  const notasAvaliacao = useNotasAvaliacao(idsVisiveis);

  function aoSoltar(novoStatus: StatusOrcamento) {
    const id = arrastando;
    setArrastando(null);
    setColunaAlvo(null);
    if (!id) return;
    const o = orcamentos.find((x) => x.id === id);
    if (!o || o.status === novoStatus) return;
    void aoTrocarStatus(o, novoStatus);
  }

  const porStatus = useMemo(() => {
    const map = new Map<StatusOrcamento, OrcamentoResumo[]>();
    for (const e of colunas) map.set(e.id, []);
    for (const o of orcamentos) map.get(o.status)?.push(o);
    return map;
  }, [orcamentos, colunas]);

  return (
    <div className="kanban">
      {colunas.map((etapa) => {
        const status = etapa.id;
        const itens = porStatus.get(status) ?? [];
        const total = itens.reduce((acc, o) => acc + o.total, 0);
        const ehAlvo = colunaAlvo === status && arrastando !== null;
        return (
          <div
            key={status}
            className={`kanban__column${ehAlvo ? ' kanban__column--alvo' : ''}`}
            onDragOver={(e) => {
              if (arrastando) {
                e.preventDefault();
                if (colunaAlvo !== status) setColunaAlvo(status);
              }
            }}
            onDragLeave={() => {
              if (colunaAlvo === status) setColunaAlvo(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              aoSoltar(status);
            }}
          >
            <header className="kanban__header" style={{ borderTopColor: etapa.cor }}>
              <div className="kanban__header-titulo">
                <span className="kanban__bolinha" style={{ background: etapa.cor }} />
                <span>{etapa.rotulo}</span>
              </div>
              <span className="kanban__contagem">{itens.length}</span>
            </header>
            <div className="kanban__lista">
              {itens.length === 0 ? (
                <div className="kanban__vazio">Sem OS</div>
              ) : (
                itens.map((o) => {
                  // Quais botões aparecem no card é decisão da etapa, feita
                  // em Configurações > Etapas do kanban.
                  const podeExcluir = etapa.acoes.includes('excluir');
                  const podeEditar = etapa.acoes.includes('editar');
                  const sugerirContaReceber = etapa.acoes.includes('contaReceber');
                  const podeArrastar = !ehTerminal(status);
                  const atrasada = entregaAtrasada(o.prazoEntrega, etapa.alertaAtraso);
                  const avaliacao = notasAvaliacao.get(o.id);
                  return (
                    <article
                      key={o.id}
                      className={`kanban__card${arrastando === o.id ? ' kanban__card--arrastando' : ''}${atrasada ? ' kanban__card--atrasada' : ''}`}
                      draggable={podeArrastar}
                      onDragStart={(e) => {
                        if (!podeArrastar) {
                          e.preventDefault();
                          return;
                        }
                        e.dataTransfer.effectAllowed = 'move';
                        setArrastando(o.id);
                      }}
                      onDragEnd={() => {
                        setArrastando(null);
                        setColunaAlvo(null);
                      }}
                      title={
                        podeArrastar
                          ? 'Arraste para outra coluna para mudar o status'
                          : `${etapa.rotulo}: etapa final — a ordem fica somente para consulta.`
                      }
                    >
                      <div className="kanban__card-topo">
                        <span className="kanban__card-id">
                          <strong className="kanban__card-numero">{o.numero}</strong>
                          {avaliacao && <NotaAvaliacaoKanban avaliacao={avaliacao} />}
                        </span>
                        <span className="kanban__card-total">
                          {formatarValorMonetario(o.total, ocultar)}
                        </span>
                      </div>
                      {o.syncPendente && (
                        <div
                          title="Número provisório — aguardando servidor para atribuir o número definitivo."
                          style={{
                            background: '#fef3c7',
                            color: '#92400e',
                            border: '1px solid #fde68a',
                            borderRadius: 4,
                            padding: '3px 8px',
                            fontSize: 10,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: 0.06,
                            marginBottom: 8,
                            display: 'inline-block',
                          }}
                        >
                          ⏳ Aguardando servidor
                        </div>
                      )}
                      {atrasada && (
                        <div className="kanban__card-atraso" role="alert">
                          ⚠️ Entrega atrasada
                        </div>
                      )}
                      <dl className="kanban__card-info">
                        <div>
                          <dt>Cliente</dt>
                          <dd>{o.clienteNome ?? '—'}</dd>
                        </div>
                        <div>
                          <dt>Placa</dt>
                          <dd>{o.veiculoDescricao}</dd>
                        </div>
                        <div>
                          <dt>Data de entrega</dt>
                          <dd className={atrasada ? 'kanban__card-prazo--atrasado' : undefined}>
                            {formatarData(o.prazoEntrega)}
                          </dd>
                        </div>
                        <div>
                          <dt>Responsável</dt>
                          {/* O.S. antiga aberta sem responsável: destaca para ser
                              preenchida na edição. Terminal fica como está. */}
                          {!o.colaboradorResponsavelId && !ehTerminal(o.status) ? (
                            <dd className="kanban__card-prazo--atrasado">Sem responsável</dd>
                          ) : (
                            <dd>{o.colaboradorResponsavelNome ?? '—'}</dd>
                          )}
                        </div>
                        <div>
                          <dt>Itens</dt>
                          <dd>{o.quantidadeItens}</dd>
                        </div>
                      </dl>
                      <div className="kanban__card-acoes">
                        <Button
                          className="btn--icon"
                          aria-label={`Ver ${o.numero}`}
                          onClick={() => aoVisualizar(o.id)}
                        >
                          <IconEye />
                        </Button>
                        {sugerirContaReceber && (
                          <Button
                            variante="primary"
                            className="btn--icon"
                            aria-label={`Criar conta a receber para ${o.numero}`}
                            onClick={() => aoCriarContaReceber(o.id)}
                          >
                            <IconDollar />
                          </Button>
                        )}
                        {podeEditar && (
                          <Button
                            className="btn--icon"
                            aria-label={`Editar ${o.numero}`}
                            onClick={() => aoEditar(o.id)}
                          >
                            <IconEdit />
                          </Button>
                        )}
                        {podeExcluir && (
                          <Button
                            className="btn--icon btn--danger"
                            aria-label={`Excluir ${o.numero}`}
                            onClick={() => aoExcluir(o)}
                          >
                            <IconTrash />
                          </Button>
                        )}
                      </div>
                    </article>
                  );
                })
              )}
            </div>
            {itens.length > 0 && (
              <footer className="kanban__rodape">
                {formatarValorMonetario(total, ocultar)}
              </footer>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Traduz a URL no que a tela deve mostrar. Aceita `/ordens-servico`,
 * `/ordens-servico/nova`, `/ordens-servico/:id` e `/ordens-servico/:id/editar`.
 */
type RotaOrdem =
  | { modo: 'lista' }
  | { modo: 'nova' }
  | { modo: 'detalhe'; id: string }
  | { modo: 'editar'; id: string };

function interpretarRota(pathname: string): RotaOrdem {
  const resto = pathname.replace(ROTAS.ordens, '').replace(/^\//, '');
  if (resto === '') return { modo: 'lista' };
  const [id, sufixo] = resto.split('/');
  if (id === 'nova') return { modo: 'nova' };
  if (sufixo === 'editar') return { modo: 'editar', id };
  return { modo: 'detalhe', id };
}

export function OrcamentosPage() {
  const { orcamentos, carregando, erro, recarregar, alterarStatus, excluir } = useOrcamentos();
  const { ocultar } = useOcultarValores();
  // Qual OS está aberta, e em que modo, vem da URL — assim o link de uma
  // ordem pode ser compartilhado e o voltar do navegador funciona.
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const rota = useMemo(() => interpretarRota(pathname), [pathname]);
  const criando = rota.modo === 'nova';
  const visualizandoId = rota.modo === 'detalhe' ? rota.id : null;
  const editandoId = rota.modo === 'editar' ? rota.id : null;
  // Busca + período por data de emissão da OS (vazio = "todo o período").
  // Persistidos: reabrir o programa volta com o mesmo filtro.
  const {
    busca,
    setBusca,
    dataInicio,
    setDataInicio,
    dataFim,
    setDataFim,
    aplicarPreset,
    presetAtivo,
    limparPeriodo,
  } = useFiltrosKanban();
  const [criandoContaReceberDeOrcamentoId, setCriandoContaReceberDeOrcamentoId] = useState<string | null>(null);
  // Mudança de etapa que ainda precisa de "sim, pode": guarda a OS e para
  // onde ela vai, porque a pergunta é configurável por etapa.
  const [confirmandoMudanca, setConfirmandoMudanca] = useState<{
    orcamento: OrcamentoResumo;
    etapa: EtapaKanban;
  } | null>(null);
  const dialogos = useDialogos();
  const { etapas, rotuloDe } = useEtapasKanban();

  const periodoAtivo = dataInicio !== '' || dataFim !== '';
  const buscaAtiva = busca.trim() !== '';

  const filtrados = useMemo(() => {
    const q = normalizarBusca(busca.trim());
    // Limites do período (só quando preenchidos). Filtros se somam (E lógico).
    const inicio = dataInicio ? new Date(`${dataInicio}T00:00:00`) : null;
    const fim = dataFim ? new Date(`${dataFim}T23:59:59`) : null;
    return orcamentos.filter((o) => {
      if (inicio || fim) {
        const d = new Date(o.data);
        if (inicio && d < inicio) return false;
        if (fim && d > fim) return false;
      }
      if (q !== '') {
        const alvo = normalizarBusca(
          `${o.numero} ${o.clienteNome ?? ''} ${o.veiculoDescricao} ${o.status} ${rotuloDe(o.status)} ${o.colaboradorResponsavelNome ?? ''}`,
        );
        if (!alvo.includes(q)) return false;
      }
      return true;
    });
  }, [orcamentos, busca, dataInicio, dataFim, rotuloDe]);

  async function trocarStatus(o: OrcamentoResumo, novo: StatusOrcamento) {
    if (novo === o.status) return;
    // Etapa final é irreversível — o backend também barra, aqui só evita
    // uma ida ao servidor que já se sabe que vai falhar.
    if (ehTerminal(o.status)) return;
    const destino = etapas.find((e) => e.id === novo) ?? null;
    if (destino?.confirmacao) {
      setConfirmandoMudanca({ orcamento: o, etapa: destino });
      return;
    }
    await aplicarStatus(o, novo);
  }

  async function aplicarStatus(o: OrcamentoResumo, novo: StatusOrcamento) {
    try {
      await alterarStatus(o.id, novo);
    } catch (e) {
      await dialogos.alertar(e instanceof Error ? e.message : 'Falha ao alterar status.');
    }
  }

  async function confirmarMudancaDeEtapa() {
    if (!confirmandoMudanca) return;
    const { orcamento, etapa } = confirmandoMudanca;
    setConfirmandoMudanca(null);
    await aplicarStatus(orcamento, etapa.id);
  }

  async function confirmarExclusao(o: OrcamentoResumo) {
    const ok = await dialogos.confirmar({
      titulo: 'Excluir ordem de serviço',
      mensagem: `Excluir a ordem de serviço "${o.numero}"?`,
      textoConfirmar: 'Excluir',
      perigoso: true,
    });
    if (!ok) return;
    try {
      await excluir(o.id);
    } catch (e) {
      await dialogos.alertar(e instanceof Error ? e.message : 'Falha ao excluir.');
    }
  }

  // Os cards de resumo seguem os mesmos filtros (busca + período) da lista,
  // senão mostram números que não batem com o que está na tela.
  const totalAcumulado = useMemo(
    () => filtrados.reduce((acc, o) => acc + o.total, 0),
    [filtrados],
  );
  const fechados = useMemo(
    () => filtrados.filter((o) => o.status === ETAPA_FECHADO).length,
    [filtrados],
  );
  const cancelados = useMemo(
    () => filtrados.filter((o) => o.status === ETAPA_CANCELADO).length,
    [filtrados],
  );
  // "Em andamento" = tudo que não é a primeira coluna nem uma etapa final.
  // Assim o número continua certo mesmo com etapas criadas pela oficina.
  const emAndamento = useMemo(
    () =>
      filtrados.filter((o) => o.status !== 'EmAberto' && !ehTerminal(o.status)).length,
    [filtrados],
  );

  /**
   * Colunas desenhadas: as etapas configuradas mais uma coluna de resgate
   * para cada status que aparece nos dados mas sumiu da configuração —
   * pode acontecer entre o momento em que outra máquina apaga uma etapa e
   * o sync chegar aqui. Sem isso, a OS ficaria invisível no kanban.
   */
  const colunas = useMemo<EtapaKanban[]>(() => {
    const conhecidas = new Set(etapas.map((e) => e.id));
    const orfas = new Map<string, EtapaKanban>();
    for (const o of orcamentos) {
      if (conhecidas.has(o.status) || orfas.has(o.status)) continue;
      orfas.set(o.status, {
        id: o.status,
        rotulo: `${rotuloDe(o.status)} (etapa removida)`,
        cor: '#64748b',
        acoes: ['editar'],
        confirmacao: null,
        alertaAtraso: false,
        aprovacaoDestino: null,
      });
    }
    return ordenarEtapas([...etapas, ...orfas.values()]);
  }, [etapas, orcamentos, rotuloDe]);

  if (visualizandoId) {
    return (
      <OrcamentoVisualizacaoPage
        id={visualizandoId}
        aoVoltar={() => navigate(ROTAS.ordens)}
        aoEditar={(id) => navigate(ROTAS.ordemEditar(id))}
      />
    );
  }

  if (criando) {
    return (
      <OrcamentoNovoPage
        aoCancelar={() => navigate(ROTAS.ordens)}
        aoSalvar={async () => {
          navigate(ROTAS.ordens);
          await recarregar();
        }}
      />
    );
  }

  if (editandoId) {
    return (
      <OrcamentoNovoPage
        idEdicao={editandoId}
        aoCancelar={() => navigate(ROTAS.ordem(editandoId))}
        aoSalvar={async () => {
          navigate(ROTAS.ordens);
          await recarregar();
        }}
      />
    );
  }

  return (
    <section className="page">
      <PageHeader
        titulo="Ordens de serviço"
        descricao="Acompanhe as ordens de serviço por status e valor."
        acoes={
          <>
            <BotaoOcultarValores />
            <Button onClick={() => void recarregar()} disabled={carregando}>
              <IconRefresh className="btn__icon" />
              {carregando ? 'Atualizando…' : 'Atualizar'}
            </Button>
            <Button variante="primary" onClick={() => navigate(ROTAS.ordensNova)}>
              + Nova ordem de serviço
            </Button>
          </>
        }
      />

      {erro && (
        <p role="alert" className="alert alert--error">
          Falha ao carregar: {erro}
        </p>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <span className="stat-card__label">Total de OS</span>
          <span className="stat-card__value">{filtrados.length}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Em andamento</span>
          <span className="stat-card__value">{emAndamento}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">{rotuloDe(ETAPA_FECHADO)}</span>
          <span className="stat-card__value">{fechados}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">{rotuloDe(ETAPA_CANCELADO)}</span>
          <span className="stat-card__value">{cancelados}</span>
        </div>
        <div className="stat-card">
          <span className="stat-card__label">Valor acumulado</span>
          <span className="stat-card__value">
            {formatarValorMonetario(totalAcumulado, ocultar)}
          </span>
        </div>
      </div>

      <div className="card">
        <div className="card__header card__header--filtros">
          <span className="card__title">
            {periodoAtivo || buscaAtiva
              ? `${filtrados.length} de ${orcamentos.length} ordem(ns) de serviço`
              : 'Propostas recentes'}
          </span>
          <div className="kanban-filtros">
            <SearchInput
              valor={busca}
              onChange={setBusca}
              placeholder="Buscar por nº, cliente, placa ou status…"
              ariaLabel="Buscar ordem de serviço"
            />
            <div
              className="kanban-filtros__periodo"
              role="group"
              aria-label="Filtrar por período de emissão"
            >
              <div className="segmented" role="group" aria-label="Atalhos de período">
                <button
                  type="button"
                  className={`segmented__item${presetAtivo('hoje') ? ' segmented__item--ativo' : ''}`}
                  aria-pressed={presetAtivo('hoje')}
                  onClick={() => aplicarPreset('hoje')}
                >
                  Hoje
                </button>
                <button
                  type="button"
                  className={`segmented__item${presetAtivo('7d') ? ' segmented__item--ativo' : ''}`}
                  aria-pressed={presetAtivo('7d')}
                  onClick={() => aplicarPreset('7d')}
                >
                  7 dias
                </button>
                <button
                  type="button"
                  className={`segmented__item${presetAtivo('mes') ? ' segmented__item--ativo' : ''}`}
                  aria-pressed={presetAtivo('mes')}
                  onClick={() => aplicarPreset('mes')}
                >
                  Este mês
                </button>
              </div>
              <div className="kanban-filtros__datas">
                <input
                  className="kanban-filtros__data"
                  type="date"
                  value={dataInicio}
                  max={dataFim || undefined}
                  onChange={(e) => setDataInicio(e.target.value)}
                  aria-label="Data inicial do período"
                  title="Data inicial (emissão)"
                />
                <span className="kanban-filtros__ate" aria-hidden>
                  até
                </span>
                <input
                  className="kanban-filtros__data"
                  type="date"
                  value={dataFim}
                  min={dataInicio || undefined}
                  onChange={(e) => setDataFim(e.target.value)}
                  aria-label="Data final do período"
                  title="Data final (emissão)"
                />
              </div>
              {periodoAtivo && (
                <button
                  type="button"
                  className="kanban-filtros__limpar"
                  onClick={limparPeriodo}
                  aria-label="Limpar período"
                  title="Limpar período"
                >
                  <IconTrash />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="card__body card__body--flush">
          {orcamentos.length === 0 && !carregando && !erro ? (
            <EmptyState
              icone={<IconInbox />}
              titulo="Nenhuma ordem de serviço cadastrada ainda."
              descricao="Crie uma ordem de serviço para começar a acompanhar os trabalhos."
            />
          ) : filtrados.length === 0 ? (
            <EmptyState
              icone={<IconInbox />}
              titulo="Nenhuma ordem de serviço corresponde aos filtros."
              descricao="Tente outro termo, ajuste o período ou limpe os filtros."
            />
          ) : (
            <Kanban
              orcamentos={filtrados}
              colunas={colunas}
              aoVisualizar={(id) => navigate(ROTAS.ordem(id))}
              aoEditar={(id) => navigate(ROTAS.ordemEditar(id))}
              aoCriarContaReceber={setCriandoContaReceberDeOrcamentoId}
              aoExcluir={(o) => void confirmarExclusao(o)}
              aoTrocarStatus={trocarStatus}
            />
          )}
        </div>
      </div>

      {criandoContaReceberDeOrcamentoId && (
        <CriarContaReceberDoOrcamentoModal
          orcamentoId={criandoContaReceberDeOrcamentoId}
          aoFechar={() => setCriandoContaReceberDeOrcamentoId(null)}
        />
      )}

      {/*
        A pergunta é escrita pela oficina em Configurações › Etapas do
        kanban — título, texto e aviso saem de lá. "Fechado pago" e
        "Cancelado" sempre têm uma; as demais só se o usuário quiser.
      */}
      <Modal
        aberto={Boolean(confirmandoMudanca)}
        titulo={confirmandoMudanca?.etapa.confirmacao?.titulo ?? 'Confirmar mudança de etapa?'}
        onFechar={() => setConfirmandoMudanca(null)}
        rodape={
          <>
            <Button onClick={() => setConfirmandoMudanca(null)}>Cancelar</Button>
            <Button variante="primary" onClick={() => void confirmarMudancaDeEtapa()}>
              Confirmar
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <p>
            {confirmandoMudanca &&
              textoConfirmacao(confirmandoMudanca.etapa.confirmacao?.mensagem ?? '', {
                numero: confirmandoMudanca.orcamento.numero,
                etapa: confirmandoMudanca.etapa.rotulo,
              })}
          </p>
          {confirmandoMudanca?.etapa.confirmacao?.aviso && (
            <div className="form-error" role="alert">
              {confirmandoMudanca.etapa.confirmacao.aviso}
            </div>
          )}
        </div>
      </Modal>
    </section>
  );
}
